import { useCallback, useState } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { UserRejectedRequestError, BaseError } from 'viem';
import { useWriteContract, usePublicClient } from 'wagmi';

import { predictionContractAbi } from '@/abi';
import { I_ProfilePredictionDialogContentProps } from '@/ts/interfaces/I_ProfilePredictionDialogContentProps';

export const useClaimWinningsModal = ({
  predictionAddress,
  selectedParticipation,
  onClaimingStateChange,
  closeModal,
  onClaimSuccess
}: Pick<
  I_ProfilePredictionDialogContentProps,
  | 'predictionAddress'
  | 'selectedParticipation'
  | 'onClaimingStateChange'
  | 'closeModal'
> & {
  onClaimSuccess?: () => void;
}) => {
  const t = useTranslations();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const [isClaiming, setIsClaiming] = useState(false);

  const handleClaimWinnings = useCallback(async () => {
    if (!predictionAddress || !selectedParticipation?.claimData || isClaiming) {
      return;
    }

    const { claimData } = selectedParticipation;
    const { predictionId, rewardAmount, merkleProofs } = claimData;

    try {
      setIsClaiming(true);
      onClaimingStateChange?.(true);

      const merkleProof = merkleProofs.map(
        (proof: string) => proof as `0x${string}`
      );

      const txHash = await writeContractAsync({
        address: predictionAddress as `0x${string}`,
        abi: predictionContractAbi,
        functionName: 'claimWinnings',
        args: [BigInt(predictionId), BigInt(rewardAmount), merkleProof]
      });

      if (publicClient) {
        await publicClient.waitForTransactionReceipt({
          hash: txHash,
          confirmations: 2
        });
      }

      toast.success(t('profile.predictionWinners.claimSuccess'));

      // Call onClaimSuccess callback to refresh data
      if (onClaimSuccess) {
        onClaimSuccess();
      }

      if (closeModal) {
        closeModal();
      }
    } catch (error) {
      const isUserRejection =
        error instanceof UserRejectedRequestError ||
        (error instanceof BaseError &&
          String(error.cause).toLowerCase().includes('user rejected'));

      if (!isUserRejection) {
        toast.error(t('profile.predictionWinners.claimError'));
      }
      console.error('Claim winnings error:', error);
    } finally {
      setIsClaiming(false);
      onClaimingStateChange?.(false);
    }
  }, [
    predictionAddress,
    selectedParticipation,
    writeContractAsync,
    publicClient,
    isClaiming,
    closeModal,
    t,
    onClaimingStateChange,
    onClaimSuccess
  ]);

  return { handleClaimWinnings, isClaiming };
};
